// The order an order's lines are listed in wherever the whole order prints: the pick
// sheet, the QuickBooks invoice and the order-summary page (one rule, so the three agree
// line for line). It is the tenant's own order, as the Master Menu has it:
//  1. the category's place in the tenant's category list (categories.sort_order, then
//     name — what GET /categories returns); a line whose category is not in the list
//     goes last;
//  2. within a category, the menu's product order (products.sort_order, then name);
//  3. an item's units stay together, where the first of them sits — in the order the
//     admin dragged them into (unit_order), else the lead first, then the largest share
//     of a case (the menu card's order: groupProductSizes).
// Before 2026-10-16 the three places each held a copy of one hardcoded list of category
// NAMES ("Fruit", "Citrus", "Lettuce & Greens"…). A tenant whose categories were named
// otherwise ("Citrus Fruits", "Greens") had most lines fall into one alphabetical pile
// at the end — Iceberg and Romaine split by oranges, bananas and onions on Pitt's sheet.
// Pure functions only; shared by the server and the client.
import { groupProductSizes } from "./productSizes";

export type SortableLineProduct = {
  id: number;
  name: string;
  categoryId?: number | null;
  sortOrder?: number | null;
  sizeOfProductId?: number | null;
  unitOrder?: number | null;
  caseFraction?: string | null;
};

export type SortableLine = { product?: SortableLineProduct | null };

/**
 * Each category's place in the tenant's list, by id. The list is put in the tenant's
 * order first (sort_order, then name), whatever order it arrived in.
 */
export function categoryRanks(list: Array<{ id: number; name: string; sortOrder?: number | null }>): Map<number, number> {
  const ordered = list
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
  return new Map(ordered.map((category, index) => [category.id, index]));
}

const LAST = Number.MAX_SAFE_INTEGER;

/** A new array: the lines in the tenant's order (see the top of this file). */
export function sortOrderLines<L extends SortableLine>(lines: L[], ranks: Map<number, number>): L[] {
  const rankOf = (line: L) => {
    const categoryId = line.product?.categoryId;
    return categoryId == null ? LAST : ranks.get(categoryId) ?? LAST;
  };
  const byMenu = (a: L, b: L) =>
    (a.product?.sortOrder ?? 0) - (b.product?.sortOrder ?? 0) || (a.product?.name ?? "").localeCompare(b.product?.name ?? "");

  // One bucket per category, in the tenant's order; the lines of each in menu order.
  const buckets = new Map<number, L[]>();
  for (const line of lines) {
    const rank = rankOf(line);
    const bucket = buckets.get(rank);
    if (bucket) bucket.push(line);
    else buckets.set(rank, [line]);
  }

  const sorted: L[] = [];
  for (const rank of Array.from(buckets.keys()).sort((a, b) => a - b)) {
    const bucket = buckets.get(rank)!.slice().sort(byMenu);
    // An item's units collapse to where the first of them sits, in the menu card's order.
    // A line with no product stands alone: it is given an id no product has.
    const members = bucket.map((line, index) => ({
      id: line.product?.id ?? -(index + 1),
      sizeOfProductId: line.product?.sizeOfProductId ?? null,
      caseFraction: line.product?.caseFraction ?? null,
      unitOrder: line.product?.unitOrder ?? null,
      line,
    }));
    for (const group of groupProductSizes(members)) {
      for (const member of group.members) sorted.push(member.line);
    }
  }
  return sorted;
}
